<template>
  <b-container fluid class="p-0 mb-5">
    <b-row align="left" class="mb-4">
      <b-col cols="12">
        <h1>
          Asset <code>{{ assetId }}</code>
        </h1>
      </b-col>
    </b-row>
    <b-row>
      <b-col cols="6" v-if="canGetAsset">
        <AssetDetails
          v-if="!getIsLoading"
          :payload="asset"
          :metadataMapping="metadataMapping"
          :assetId="assetId"
          :linkedDevices="linkedDevices"
          :canUpdateAsset="canUpdateAsset"
          :canGetAssetsMapping="canGetAssetsMapping"
          :canUnlinkDevice="canUnlinkDevice"
          :canMLinkDevices="canMLinkDevices"
          :canSearchDevices="canSearchDevices"
          @saveMetadata="onSaveMetadata"
          @saveInfos="onSaveAssetInfos"
          @unlink="onUnlinkDevice"
          @link="onLinkDevices"
        />
      </b-col>
      <b-col cols="6" v-else>
        <b-card>
          <h1 class="float-left display-3 mr-4">
            <i class="fa fa-lock"></i>
          </h1>
          <h4 class="pt-3">You are not allowed to see asset informations.</h4>
        </b-card>
      </b-col>
      <b-col cols="6" v-if="canSearchAssetHistory">
        <b-card no-body class="h-100" v-if="!getIsLoading">
          <b-tabs
            v-if="measureTypes.length > 0"
            card
            @activate-tab="onTabChanged"
            class="Asset-measures h-100"
            content-class="Asset-measures-tabcontent"
          >
            <b-tab
              v-for="type of measureTypes"
              :data-cy="`MeasureHistory-${type}`"
              :key="type"
              :title="type"
              class="h-100 p-0"
            >
              <AssetHistoryMap
                v-if="type === 'position' && currentTabType === 'position'"
                :currentTabType="currentTabType"
                class="h-100"
                :asset="asset"
                :measures="tableMeasures"
                :fields="fields"
                :current-page="currentPage"
                :per-page="perPage"
                :total-rows="totalRows"
                @page-changed="v => onPageChanged(v, type)"
                @per-page-changed="v => onPerPageChanged(v, type)"
              />
              <Table
                v-else
                class="py-3 text-left"
                :current-page="currentPage"
                :fields="fields"
                :items="tableMeasures"
                :per-page="perPage"
                :total-rows="totalRows"
                @page-changed="v => onPageChanged(v, type)"
                @per-page-changed="v => onPerPageChanged(v, type)"
              >
              </Table>
            </b-tab>
          </b-tabs>
          <div v-else>
            <h4 class="mt-3">
              This asset has no measures yet.
            </h4>
            <span
              >Once your asset is linked to a device and we receive its
              measurement payloads, you will see them here.</span
            >
          </div>
        </b-card>
      </b-col>
      <b-col cols="6" v-else>
        <b-card>
          <h1 class="float-left display-3 mr-4">
            <i class="fa fa-lock"></i>
          </h1>
          <h4 class="pt-3">You are not allowed to see asset history.</h4>
        </b-card>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import _ from 'lodash';
import AssetDetails from '@/components/device-manager/asset/AssetDetails';
import AssetHistoryMap from '@/components/device-manager/asset/AssetHistoryMap.vue';
import { mapGetters } from 'vuex';
import {
  canGetAssetsMapping,
  canSearchDevices,
  canGetAsset,
  canSearchAssetHistory,
  canMLinkDevices,
  canUnlinkDevice,
  canUpdateAsset
} from '@/helpers/rights.js';
import {
  tenantId,
  assetOrDevice as asset,
  type,
  model,
  reference,
  measureTypes,
  currentTabType,
  currentMeasurePath,
  tableMeasures,
  measures,
  currentHistoryTabIdx
} from '@/composition/asset-device';
import { lte } from 'lodash';
import { computed } from '@vue/composition-api';

export default {
  name: 'Asset',
  setup(props, { root }) {
    return {
      tenantId,
      asset,
      type,
      model,
      reference,
      measureTypes,
      currentTabType,
      currentMeasurePath,
      tableMeasures,
      measures,
      currentHistoryTabIdx
    };
  },
  components: {
    AssetDetails,
    AssetHistoryMap
  },
  data() {
    return {
      linkedDevices: [],
      metadataMapping: null,
      currentPage: 1,
      perPage: 15,
      totalRows: 0,
      fields: [
        {
          key: 'date',
          label: 'Date',
          sortable: false
        },
        {
          key: 'measure',
          label: 'Measure',
          sortable: false
        }
      ]
    };
  },
  computed: {
    ...mapGetters('tenants', ['selectedTenant']),
    ...mapGetters('app', ['getIsLoading']),
    assetId() {
      return _.get(this.$route, 'params.assetId', null);
    },
    canGetAssetsMapping,
    canSearchDevices,
    canGetAsset,
    canSearchAssetHistory,
    canMLinkDevices,
    canUnlinkDevice,
    canUpdateAsset
  },
  watch: {
    currentTabType: {
      handler() {
        this.currentPage = 1;
        this.fetchMeasures();
      }
    },
    tenantId: {
      async handler(value) {
        if (value) {
          await this.initView();
        }
      },
      immediate: true
    }
  },
  beforeMount() {
    this.$store.commit('app/SET_LOADING');
  },
  methods: {
    async initView() {
      this.$store.commit('app/SET_LOADING');
      this.fetchAssetMapping();
      await this.fetchAssetInfos();
      await this.fetchMeasures();
      await this.fetchLinkedDevices();
      this.$store.commit('app/UNSET_LOADING');
    },

    async fetchAssetMapping() {
      if (!this.canGetAssetsMapping) {
        return;
      }
      try {
        const mapping = await this.$kuzzle.collection.getMapping(
          tenantId.value,
          'assets'
        );
        this.metadataMapping = mapping.properties.metadata;
      } catch (error) {
        this.$bvToast.toast(error.message, {
          title: 'Something went wrong while fetching asset metadata mapping',
          noAutoHide: true,
          dismissable: true,
          variant: 'danger',
          toaster: 'b-toaster-bottom-right'
        });
        this.$log.error(error);
      }
    },
    async fetchLinkedDevices() {
      if (!this.canSearchDevices) {
        return;
      }
      let query = {
        equals: {
          assetId: this.assetId
        }
      };
      try {
        const response = await this.$kuzzle.query({
          index: tenantId.value,
          controller: 'device-manager/device',
          action: 'search',
          body: {
            query
          },
          size: 100,
          lang: 'koncorde'
        });
        this.linkedDevices = response.result.hits.map(d => ({
          id: d._id,
          measureTypes: Object.keys(_.get(d, '_source.measures', {})),
          reference: d._source.reference
        }));
      } catch (error) {
        this.$log.error(error);
        this.$bvToast.toast(error.message, {
          title: 'Something went wrong while fetching linked devices',
          noAutoHide: true,
          dismissable: true,
          variant: 'danger',
          toaster: 'b-toaster-bottom-right'
        });
      }
    },
    async fetchAssetInfos() {
      if (!this.canGetAsset) {
        // @TODO redirect to not allowed view
        return;
      }
      try {
        const assetRes = await this.$kuzzle.document.get(
          tenantId.value,
          'assets',
          this.assetId
        );
        asset.value = assetRes;
      } catch (error) {
        // @TODO redirect to 404 asset not found
        this.$log.error(error);
        this.$bvToast.toast(error.message, {
          title: 'Something went wrong while fetching asset information',
          noAutoHide: true,
          dismissable: true,
          variant: 'danger',
          toaster: 'b-toaster-bottom-right'
        });
      }
    },
    async fetchMeasures() {
      if (!this.canSearchAssetHistory || measureTypes.value.length === 0) {
        return;
      }
      try {
        const searchBody = {
          query: {
            and: [
              {
                equals: {
                  assetId: this.assetId
                }
              },
              {
                equals: {
                  measureTypes: currentTabType.value
                }
              }
            ]
          },
          sort: {
            [`asset.measures.${currentTabType.value}.updatedAt`]: {
              order: 'desc',
              nested: {
                path: 'asset'
              }
            }
          }
        };
        const searchResult = await this.$kuzzle.document.search(
          tenantId.value,
          'asset-measures-history',
          searchBody,
          {
            from: (this.currentPage - 1) * this.perPage,
            size: this.perPage,
            lang: 'koncorde'
          }
        );
        this.totalRows = searchResult.total;
        measures.value = searchResult.hits;
      } catch (error) {
        this.$log.error(error);
        this.$bvToast.toast(error.message, {
          title: 'Something went wrong while fetching asset history',
          noAutoHide: true,
          dismissable: true,
          variant: 'danger',
          toaster: 'b-toaster-bottom-right'
        });
      }
    },

    onNumberOfPointsChanged(value) {
      this.perPage = value;
      this.fetchMeasures();
    },
    async onLinkDevices(devices) {
      if (!this.canMLinkDevices) {
        return;
      }
      this.$store.commit('app/SET_LOADING');
      try {
        const result = await this.$kuzzle.query({
          controller: 'device-manager/device',
          action: 'mLink',
          body: {
            records: devices.map(d => ({ assetId: this.assetId, deviceId: d }))
          },
          refresh: 'wait_for'
        });
        this.initView();
      } catch (error) {
        this.$bvToast.toast(error.message, {
          title: 'Something went wrong while linking devices',
          noAutoHide: true,
          dismissable: true,
          variant: 'danger',
          toaster: 'b-toaster-bottom-right'
        });
        this.$log.error(error);
      }
      this.$store.commit('app/UNSET_LOADING');
    },
    async onUnlinkDevice(deviceId) {
      if (!this.canUnlinkDevice) {
        return;
      }
      const device = this.linkedDevices.find(d => d.id === deviceId);
      if (!device) {
        this.$log.warn(
          `Could not retrieve measure types from ${deviceId}. Unable to find it in the linked devices array.`
        );
      }
      const h = this.$createElement;
      const messageVNode = h('div', { class: ['foobar'] }, [
        h('p', { class: [] }, [
          'Unlink device ',
          h('code', deviceId),
          ' from asset ',
          h('code', this.assetId),
          ' ?'
        ]),
        device && device.measureTypes.length
          ? h('small', { class: [] }, [
              'This device provides measures of ',
              device.measureTypes.map(t => h('b-badge', { class: ['mr-1'] }, t))
            ])
          : h('div')
      ]);
      const value = await this.$bvModal.msgBoxConfirm([messageVNode], {
        title: 'Unlink confirmation',
        footerClass: 'p-2',
        hideHeaderClose: true,
        centered: true,
        okTitle: 'Unlink'
      });
      if (!value) {
        return;
      }
      this.$store.commit('app/SET_LOADING');
      try {
        await this.$kuzzle.query({
          index: tenantId.value,
          controller: 'device-manager/device',
          action: 'unlink',
          _id: deviceId,
          refresh: 'wait_for'
        });
        this.initView();
      } catch (error) {
        this.$bvToast.toast(error.message, {
          title: 'Something went wrong while unlinking devices',
          noAutoHide: true,
          dismissable: true,
          variant: 'danger',
          toaster: 'b-toaster-bottom-right'
        });
        this.$log.error(error);
      }
      this.$store.commit('app/UNSET_LOADING');
    },
    async onSaveAssetInfos(infos) {
      if (!this.canUpdateAsset) {
        return;
      }
      this.$store.commit('app/SET_LOADING');
      try {
        const result = await this.$kuzzle.document.update(
          tenantId.value,
          'assets',
          this.assetId,
          infos
        );
        asset.value._source.type = infos.type;
        asset.value._source.model = infos.model;
        asset.value._source.reference = infos.reference;
      } catch (error) {
        this.$bvToast.toast(error.message, {
          title: 'Something went wrong while updating asset',
          noAutoHide: true,
          dismissable: true,
          variant: 'danger',
          toaster: 'b-toaster-bottom-right'
        });
        this.$log.error(error);
      }
      this.$store.commit('app/UNSET_LOADING');
    },
    async onSaveMetadata(metadata) {
      if (!this.canUpdateAsset) {
        return;
      }
      this.$store.commit('app/SET_LOADING');
      try {
        const result = await this.$kuzzle.document.update(
          tenantId.value,
          'assets',
          this.assetId,
          {
            metadata
          }
        );
        asset._source.metadata = metadata;
      } catch (error) {
        this.$bvToast.toast(error.message, {
          title: 'Something went wrong while updating asset',
          noAutoHide: true,
          dismissable: true,
          variant: 'danger',
          toaster: 'b-toaster-bottom-right'
        });
        this.$log.error(error);
      }
      this.$store.commit('app/UNSET_LOADING');
    },
    onTabChanged(tabIdx) {
      this.currentHistoryTabIdx = tabIdx;
      this.perPage = 15;
      this.totalRows = 0;
      this.currentPage = 1;
    },
    onPerPageChanged(value, type) {
      this.perPage = value;
      this.fetchMeasures(type);
    },
    onPageChanged(page, type) {
      this.currentPage = page;
      this.fetchMeasures(type);
    }
  }
};
</script>

<style>
.Asset-measures {
  display: flex;
  flex-direction: column;
}

.Asset-measures-tabcontent {
  flex-grow: 1;
}
</style>
